import { Box, Stack } from "@mui/material";
import { useRelatedProductsQuery } from "src/api/client/related-products.api";
import { IWikifolioDetailDict } from "src/api/server/wf-detail.types";
import { ExpandableRelatedWikifoliosList } from "src/components/pages/detail/related-wikifolios/expandable-related-wikifolios-list";
import { RelatedWikifolioCard } from "src/components/pages/detail/related-wikifolios/related-wikifolio-card";
import { RelatedWikifoliosLoading } from "src/components/pages/detail/related-wikifolios/related-wikifolios-loading";
import { groupRelatedWikifoliosByLabel } from "src/components/pages/detail/related-wikifolios/related-wikifolios.helpers";
import { ArrowLink } from "src/components-mui/base/arrow-link/arrow-link";
import { Typography } from "src/components-mui/base/typography/typography";
import { PopoverButtonIconInfo } from "src/components-mui/base/wf-popover/popover-button-icon-info";
import { WfPopover } from "src/components-mui/base/wf-popover/wf-popover";
import { COUNTRY_DE } from "src/constants/locale";
import { useRouterLocale } from "src/utils/router/use-router-locale";
import { RELATED_WIKIFOLIOS_BOX_TEST_ID } from "./related-wikifolios.test-ids";

interface IRelatedWikifoliosProps {
    wikifolioId: string;
    dict: IWikifolioDetailDict["relatedWikifolios"];
}

export const RelatedWikifolios = ({ wikifolioId, dict }: IRelatedWikifoliosProps) => {
    const { country } = useRouterLocale();
    const { data: products, isLoading } = useRelatedProductsQuery(wikifolioId);

    if (isLoading) {
        return <RelatedWikifoliosLoading />;
    }

    if (!products || !dict) {
        return null;
    }

    const groupedProducts = groupRelatedWikifoliosByLabel(products.results);
    const isBaseWikifolio = groupedProducts.base?.wikifolioId === wikifolioId;

    return (
        <Box data-test-id={RELATED_WIKIFOLIOS_BOX_TEST_ID}>
            {isBaseWikifolio ? (
                <Stack
                    direction="row"
                    sx={{
                        alignItems: "center",
                        gap: 1,
                        mb: 2,
                        mt: 2,
                    }}
                >
                    <Typography
                        component="h2"
                        sx={{
                            fontSize: "2xl",
                            fontWeight: "bold",
                        }}
                    >
                        {dict.headlineBaseModelPortfolio}
                    </Typography>
                    <WfPopover
                        content={dict.headlineGliderTooltip.content}
                        link={dict.headlineGliderTooltip.link}
                        PopoverTrigger={<PopoverButtonIconInfo ariaLabel="" />}
                    />
                </Stack>
            ) : null}
            <Box
                sx={{
                    display: "flex",
                    gap: 2,
                    flexDirection: "column",
                }}
            >
                {groupedProducts.ParadiseWikifolioLumpSum && isBaseWikifolio ? (
                    <Box
                        sx={{
                            width: "100%",
                        }}
                    >
                        <ExpandableRelatedWikifoliosList
                            title={dict.subheadlineLumSumGlider}
                            products={groupedProducts.ParadiseWikifolioLumpSum}
                            imageUrl={dict.avatarImage.url}
                        />
                    </Box>
                ) : null}

                {groupedProducts.ParadiseWikifolioTargetDate && isBaseWikifolio ? (
                    <Box
                        sx={{
                            width: "100%",
                        }}
                    >
                        <ExpandableRelatedWikifoliosList
                            title={dict.subheadlineTargetDateGlider}
                            products={groupedProducts.ParadiseWikifolioTargetDate}
                            imageUrl={dict.avatarImage.url}
                        />
                    </Box>
                ) : null}

                {!isBaseWikifolio && groupedProducts.base ? (
                    <Box
                        sx={{
                            width: "100%",
                            mt: [2, 2, 0],
                        }}
                    >
                        <Typography
                            component="h3"
                            sx={{
                                fontSize: "md",
                                fontWeight: "bold",
                                mb: 1,
                            }}
                        >
                            {dict.headlineGlider}
                        </Typography>
                        <Stack
                            sx={{
                                gap: 1,
                            }}
                        >
                            <RelatedWikifolioCard
                                key={groupedProducts.base.wikifolioId}
                                title={groupedProducts.base.shortDescription}
                                href={groupedProducts.base.detailUrl}
                                imageUrl={dict.avatarImage.url}
                            />
                        </Stack>
                        {dict.linkGlider && country === COUNTRY_DE ? (
                            <Box
                                sx={{
                                    textAlign: "right",
                                    mt: 2,
                                    width: "100%",
                                }}
                            >
                                <ArrowLink href={dict.linkGlider.url} target={dict.linkGlider.target}>
                                    {dict.linkGlider.name}
                                </ArrowLink>
                            </Box>
                        ) : null}
                    </Box>
                ) : null}
            </Box>
        </Box>
    );
};
